import {
  AnalysisType,
  PickPoints,
  SegmentationControllerType,
  SegmentationProps,
} from 'config/analysisConfig';
import {
  axesLineRenderer,
  SurfaceContext,
  BufferPoolItem,
  handleNodeChange,
  Actors,
  VRContext,
  getTaviCenterTangentAtIndex,
  getPoolPolyDataBounds,
  fitActorToCanvas,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { scheduleRender } from '@features/analysis/rendering/Panels/renderState';
import { hexToNormalizedRGB } from 'config/colorUtils';
import { configStore } from 'config/configStore';
import type { TaviPlaneConfig } from 'config/config';

import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import { Vector3 } from '@kitware/vtk.js/types';
import {
  createCircleSource,
  createLineAndPointPolyData,
} from './vtkMarker';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';
import vtkPoints from '@kitware/vtk.js/Common/Core/Points';
import vtkCellArray from '@kitware/vtk.js/Common/Core/CellArray';
import { updateLinePolyDataOnActor, updateSurfaceActorProps } from './Surface3DUtilities';
import { mat3, mat4, vec3, vec4 } from 'gl-matrix';
import { TaviParsedData } from '@features/analysis/rendering/Panels/Renderers/taviDataUtils';
import { getVolumeFromSeriesId } from '@features/analysis/rendering/Panels/Renderers/CornerstoneManager';
import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import { FFR_OPACITY, FFRNames, loadKeys, nodeNames } from 'config/render3DConfig';
import vtkRenderWindow from '@kitware/vtk.js/Rendering/Core/RenderWindow';
import vtkCamera from '@kitware/vtk.js/Rendering/Core/Camera';

type TaviCenterKey = 'annulus' | 'sinus' | 'sinotubularjunction';

const TAVI_CENTER_KEYS: readonly TaviCenterKey[] = [
  'annulus',
  'sinus',
  'sinotubularjunction',
] as const;

const TAVI_CENTER_KEY_BY_ACTOR_KEY: Record<string, TaviCenterKey> = {
  [nodeNames.nodeAnnulus]: 'annulus',
  [nodeNames.nodeSihus]: 'sinus',
  [nodeNames.nodeSinotubular]: 'sinotubularjunction',
};

function createCenterLine(
  overlayRenderer: vtkRenderer,
  polyData: vtkPolyData,
  front: number,
  back: number,
  actor?: vtkActor,
) {
  if (!actor) {
    actor = vtkActor.newInstance();
    actor.getProperty().setColor(0, 1, 0);
    actor.getProperty().setInterpolationToPhong();
    actor.setScale(-1, 1, 1);
    actor.setPickable(true);
    actor.getProperty().setLighting(false);
    overlayRenderer.addActor(actor);
  }
  const points = polyData.getPoints().getData();
  const flat = [];

  for (let i = front; i < back; i += 3) {
    flat.push(points[i]);
    flat.push(points[i + 1]);
    flat.push(points[i + 2]);
  }
  const count = flat.length / 3;
  const cells: number[] = [];
  cells.push(count);
  for (let i = 0; i < count; i++) {
    cells.push(i);
  }
  updateLinePolyDataOnActor(actor, flat, cells);
  return actor;
}

const readTaviVecAtIndex = (
  arrLike: unknown,
  idx: number,
): [number, number, number] | null => {
  if (typeof idx !== 'number' || idx < 0) return null;

  const arr =
    arrLike instanceof Float64Array
      ? arrLike
      : arrLike instanceof ArrayBuffer
        ? new Float64Array(arrLike)
        : ArrayBuffer.isView(arrLike)
          ? new Float64Array(
              arrLike.buffer,
              arrLike.byteOffset,
              Math.floor(arrLike.byteLength / Float64Array.BYTES_PER_ELEMENT),
            )
          : null;

  if (!arr) return null;
  const p = idx * 3;
  if (p + 2 >= arr.length) return null;

  const x = Number(arr[p + 0]);
  const y = Number(arr[p + 1]);
  const z = Number(arr[p + 2]);
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
    return null;
  }

  return [x, y, z];
};

function getParsedTaviPlane(
  parsed: TaviParsedData | null | undefined,
  key: TaviCenterKey,
): TaviPlaneConfig | null {
  if (!parsed) return null;
  const row = parsed[key] as { centerIdx?: number } | null | undefined;
  const centerIdx = Number(row?.centerIdx);
  if (!Number.isFinite(centerIdx)) return null;

  const center = readTaviVecAtIndex(parsed.centerline, centerIdx);
  const normal = readTaviVecAtIndex(parsed.centerTangent, centerIdx);
  const dx = readTaviVecAtIndex(parsed.center_dx, centerIdx);
  const dy = readTaviVecAtIndex(parsed.center_dy, centerIdx);

  if (!center || !normal || !dx || !dy) return null;

  return { center, normal, dx, dy };
}

function getTaviPlaneOverrideOrParsed(
  pool: BufferPoolItem,
  key: TaviCenterKey,
): TaviPlaneConfig | null {
  const override = configStore.get().taviPlaneOverrides?.[key];
  if (override) return override;

  const parsed = pool?.['tavi.parsed']?.data as TaviParsedData | null | undefined;
  return getParsedTaviPlane(parsed, key);
}

const getCenterLineActorKey = (
  analysisType: AnalysisType,
  id: number,
): string => {
  const isSpecialType =
    analysisType === 'fai' || analysisType === 'calciumScore';

  return isSpecialType
    ? `${nodeNames.centerLine}.${id}`
    : `${nodeNames.singleCenterLine}`;
};

export function centerLineRender(
  context: React.MutableRefObject<SurfaceContext | VRContext | null>,
  pool: BufferPoolItem,
  analysisType: AnalysisType,
  branchIds: number[],
) {
  if (!context.current || !pool) return;
  const centerData = pool?.centerData.data as Record<number, vtkPolyData>;
  const { viewportId, renderer, overlayRenderer, actorIds } = context.current;
  if (!overlayRenderer || !centerData) return;

  const front = configStore.get().coronary_front_expand;
  const back = configStore.get().coronary_back_expand;
  overlayRenderer.setActiveCamera(renderer.getActiveCamera());

  const isAortaCenterline = analysisType === 'tavi';
  const aortaId = configStore.get().aorta_id;
  const startIdx = isAortaCenterline ? 0 : front * 3;

  // --- center line ---
  for (const id of branchIds) {
    if (!isAortaCenterline && Number(id) === aortaId) continue;
    const polyData = centerData[id];
    if (!polyData) continue;
    const points = polyData.getPoints().getData();
    const actorKey = getCenterLineActorKey(analysisType, id);
    const actor = createCenterLine(
      overlayRenderer,
      polyData,
      startIdx,
      isAortaCenterline ? points.length : points.length - back * 3,
      actorIds[actorKey] as vtkActor,
    );
    context.current.actorIds[actorKey] = actor;
  }

  if (analysisType === 'stenosis') {
    const colors = configStore.get().colors;
    const stenosisLesionInfo = configStore.get().stenosisLesionInfo;

    // --- stenosis (aorta에서는 안함) ---
    for (const id in centerData) {
      if (Number(id) === aortaId) continue;
      const polyData = centerData[id];
      if (!polyData) continue;

      const stenosisarea = stenosisLesionInfo[id];
      if (!stenosisarea) continue;

      stenosisarea.forEach((data, i) => {
        const [start, , end, val] = data;
        if (val === 0 || !context.current) return null;
        const actorKey = `${nodeNames.stenosis}.${id}.${i}`;
        const color =
          val <= 0.24
            ? colors.autoLesion[0]
            : val <= 0.49
              ? colors.autoLesion[1]
              : val <= 0.69
                ? colors.autoLesion[2]
                : colors.autoLesion[3];
        const actor = createCenterLine(
          overlayRenderer,
          polyData,
          (start + front) * 3,
          (end + front) * 3,
          actorIds[actorKey] as vtkActor,
        );
        actor.getProperty().setLineWidth(5);
        actor.getProperty().setColor(hexToNormalizedRGB(color));
        actor.modified();
        context.current.actorIds[actorKey] = actor;
      });
    }
  }

  scheduleRender(viewportId, { type: 'updateRenderWindow' });
}

export async function CPRNodeAllRenderer(
  context: React.MutableRefObject<SurfaceContext | VRContext | null>,
  pool: BufferPoolItem,
  branch: number[],
  stateMap: Record<number, PickPoints[]>,
) {
  if (!pool.centerData.data) {
    console.log('centerData 없음');
    return;
  }

  const colors = configStore.get().colors;
  const colorMap = [
    hexToNormalizedRGB(colors.Proximal),
    hexToNormalizedRGB(colors.Stenosis),
    hexToNormalizedRGB(colors.Distal),
  ];
  const renderNode = (id: number, renderNodeIndex: number) => {
    const nodes = stateMap[id];
    if (!nodes) return;
    const updatedNodes = nodes.map((node, index) => {
      const centerlineData = (
        pool.centerData.data as Record<number, vtkPolyData>
      )[id];
      if (!centerlineData) return node;
      const nodedata = handleNodeChange(centerlineData, node.index);
      if (nodedata === null) return node;
      const { position, normal } = nodedata;
      return {
        ...node,
        position,
        normal,
      };
    });
    CPRNodeRenderer(
      context,
      renderNodeIndex,
      updatedNodes,
      'total',
      colorMap,
      false, //renderNodeIndex === 0,
    );
  };

  branch.forEach((branchId, i) => {
    renderNode(branchId, i);
  });
}

export async function CPRNodeRenderer(
  context: React.MutableRefObject<SurfaceContext | VRContext | null>,
  // seriesId: string,
  id: number,
  Pickinfo: PickPoints[],
  which: any,
  colorMap: any,
  moveAxis: boolean,
) {
  if (!context.current || !Pickinfo || Pickinfo.length === 0) return;
  const { renderer, actorIds, viewportId, overlayRenderer } = context.current;
  for (let i = 0; i < Pickinfo.length; i++) {
    const node = Pickinfo[i];
    if (node === null) return;
    if (!node.position || !node.normal) continue;
    const world = node.position as [number, number, number];
    const actorKey =
      which === 'total'
        ? `${nodeNames.node}.${id}.${i}`
        : `${nodeNames.node}.${id}.${which}`;
    const prevActor = actorIds[actorKey] as vtkActor;
    const actor = createCircleSource(
      prevActor,
      world,
      node.normal!,
      colorMap[i],
    );
    if (!prevActor) {
      if (overlayRenderer) overlayRenderer.addActor(actor);
      else renderer.addActor(actor);
    }
    (actorIds as Actors)[actorKey] = actor;
  }

  if (moveAxis) {
    const index = which === 'total' ? 1 : 0;
    const world = Pickinfo?.[index]?.position;
    if (!world) return;
    scheduleRender('Axial', { type: 'focus', world, mode: 'mouse' });
    context.current.focus = world;
    context.current.savedTranslation = [-world[0], world[1], world[2]];
    axesLineRenderer(context);
  } else {
    requestAnimationFrame(() =>
      scheduleRender(viewportId!, { type: 'updateRenderWindow' }),
    );
  }
}

export function handleVisibility(
  context: React.MutableRefObject<SurfaceContext | VRContext | null>,
  analysisType: AnalysisType,
  key: SegmentationControllerType,
  props: Partial<SegmentationProps>,
  setSvgShowText: (visible: boolean) => void,
) {
  if (!context.current || !props) return;
  const { viewportId } = context.current;
  const nodeType =
    analysisType === 'aorticValveCalcium' || analysisType === 'tavi'
      ? 'aorta'
      : 'coronary';

  const visibility = props.visibility ?? false;

  if (nodeType === key) {
    const targetNodes =
      analysisType === 'aorticValveCalcium'
        ? [nodeNames.node]
        : analysisType === 'tavi'
          ? [
              nodeNames.centerLine,
              nodeNames.nodeAnnulus,
              nodeNames.nodeSinotubular,
              nodeNames.nodeSihus,
            ]
          : [nodeNames.node, nodeNames.centerLine, nodeNames.stenosis];

    setActorVisible(context, visibility, targetNodes);
    if (key === 'coronary' || key === 'aorta') {
      setSvgShowText(visibility);
    }
  }
  if (viewportId === 'Surface3D') {
    updateSurfaceActorProps(
      context as React.RefObject<SurfaceContext | null>,
      key,
      props,
    );
  }
}

export function setActorOpacity(
  context: React.MutableRefObject<SurfaceContext | VRContext | null>,
  keys: string[],
  opacity: number = 0.8,
) {
  if (!context.current) return;

  const { actorIds, renderer, overlayRenderer } = context.current;
  if (!actorIds) return;

  const actorToId = new Map<any, string>(
    Object.entries(actorIds).map(([key, actor]) => [actor, key]),
  );

  const isTargetActor = (key: string) =>
    keys.some((k) => key.includes(k));

  const resolveOpacity = (id: string) => {
    // cap은 따로 처리
    if (
      (id === FFRNames.cap_lca || id === FFRNames.cap_rca) &&
      opacity === FFR_OPACITY.FULL
    ) {
      return FFR_OPACITY.cap;
    }

    return opacity;
  };

  const applyOpacity = (actors: any[]) => {
    actors.forEach((actor) => {
      const id = actorToId.get(actor);
      if (!id || !isTargetActor(id)) return;

      actor.getProperty().setOpacity(resolveOpacity(id));
    });
  };

  applyOpacity(renderer.getActors());
  if (overlayRenderer) {
    applyOpacity(overlayRenderer.getActors());
  }
}

export function setActorVisible(
  context: React.MutableRefObject<SurfaceContext | VRContext | null>,
  visible: boolean,
  keys: string[],
) {
  if (!context.current) return;
  const { actorIds, renderer, overlayRenderer, viewportId } = context.current;
  if (!actorIds) return;

  const actorToId = new Map<any, string>(
    Object.entries(actorIds).map(([key, actor]) => [actor, key]),
  );

  const isVesselActor = (key: string) => keys.some((k) => key.includes(k));

  const applyVisibility = (actors: any[]) => {
    actors.forEach((actor) => {
      const id = actorToId.get(actor);
      if (id && isVesselActor(id)) {
        actor.setVisibility(visible);
      }
    });
  };

  applyVisibility(renderer.getActors());
  if (overlayRenderer) {
    applyVisibility(overlayRenderer.getActors());
  }
}

function syncTaviNodeActors(
  context: React.MutableRefObject<SurfaceContext | VRContext | null>,
  actorKey: string,
  world: Vector3,
  normal: Vector3,
  color: [number, number, number],
  basis?: TaviPlaneConfig,
) {
  if (!context.current) return;
  const { renderer, overlayRenderer, actorIds } = context.current;
  const pickActorSuffix = '.pick';
  const displayActorKey = actorKey.endsWith(pickActorSuffix)
    ? actorKey.slice(0, -pickActorSuffix.length)
    : actorKey;
  const pickActorKey = `${displayActorKey}${pickActorSuffix}`;

  const prevDisplayActor = actorIds[displayActorKey] as vtkActor | undefined;
  const displayActor = createCircleSource(
    prevDisplayActor,
    world as [number, number, number],
    normal as vec3,
    color,
    20,
    true,
    basis,
  );
  displayActor.setForceOpaque(true);
  displayActor.setForceTranslucent(false);
  displayActor.setPickable(false);
  if (!prevDisplayActor) {
    renderer.addActor(displayActor);
  }

  (actorIds as Actors)[displayActorKey] = displayActor;

  if (!overlayRenderer) return;
  //overlayRenderer.setActiveCamera(renderer.getActiveCamera());

  const prevPickActor = actorIds[pickActorKey] as vtkActor | undefined;
  const pickActor = createCircleSource(
    prevPickActor,
    world as [number, number, number],
    normal as vec3,
    color,
    20,
    false,
    basis,
  );
  const pickProp = pickActor.getProperty();
  pickProp.setOpacity(0.02);

  if (!prevPickActor) {
    overlayRenderer.addActor(pickActor);
  }
  (actorIds as Actors)[pickActorKey] = pickActor;
}

export function taviNodeAllRenderer(
  context: React.MutableRefObject<SurfaceContext | VRContext | null>,
  pool: BufferPoolItem,
  seriesId: string,
  type?: { id: number; name: string },
) {
  if (!context.current) return;
  const { viewportId } = context.current;

  const { imageData } = getVolumeFromSeriesId(
    seriesId,
    configStore.get().generalSetting.renderingQuality !== 'high',
  )!;

  if (!imageData) return;

  const centerData = pool?.centerData.data as Record<number, vtkPolyData>;
  if (!centerData) {
    console.log('centerData 없음');
    return;
  }

  const centerlineData = centerData[configStore.get().tavi_aorta_id];
  if (!centerlineData) {
    console.log('centerLine 없음');
    return;
  }
  const colors = configStore.get().colors;

  const color = hexToNormalizedRGB(colors.aorta);

  const keyNameByTaviCenterKey: Record<TaviCenterKey, loadKeys> = {
    annulus: nodeNames.nodeAnnulus,
    sinus: nodeNames.nodeSihus,
    sinotubularjunction: nodeNames.nodeSinotubular,
  };

  const parsed = pool?.['tavi.parsed'].data as TaviParsedData | null;
  if (!parsed) {
    console.log('parsed 없음');
    return;
  }

  TAVI_CENTER_KEYS.forEach((key) => {
    const centerIdx = parsed[key]?.centerIdx;
    if (centerIdx != null) {
      // const node = handleNodeChange(centerlineData, centerIdx);
      // if (!node) return;
      // if (!node.position || !node.normal) return;
      // const world = node.position as [number, number, number];

      const position = centerlineData.getPoints().getPoint(centerIdx) as [
        number,
        number,
        number,
      ];
      const keyName = keyNameByTaviCenterKey[key];
      const basis = getTaviPlaneOverrideOrParsed(pool, key);
      const normal =
        (basis && convertNormalToTangent(basis.normal, imageData)) ||
        getTaviCenterTangentAtIndex(pool, centerIdx, imageData);
      if (!normal || !position) {
        console.log('타타 계산실패', normal, position);
      }
      syncTaviNodeActors(
        context,
        keyName,
        position,
        normal!,
        color,
        basis ?? undefined,
      );
    }
  });

  scheduleRender(viewportId, { type: 'updateRenderWindow' });
}

export function convertNormalToTangent(
  normal: [number, number, number],
  imageData: vtkImageData,
): [number, number, number] | null {
  const spacing = imageData.getSpacing() as [number, number, number];
  const direction = imageData.getDirection() as mat3;

  const invDir =
    mat3.invert(mat3.create(), direction) ??
    mat3.transpose(mat3.create(), direction);

  const unrot = vec3.create();
  vec3.transformMat3(unrot, normal as vec3, invDir);

  const tangent = vec3.fromValues(
    unrot[0] / spacing[0],
    unrot[1] / spacing[1],
    unrot[2] / spacing[2],
  );

  if (vec3.sqrLen(tangent) < 1e-12) return null;
  vec3.normalize(tangent, tangent);
  return [tangent[0], tangent[1], tangent[2]];
}

export function taviNodeRenderer(
  context: React.MutableRefObject<SurfaceContext | VRContext | null>,
  world: Vector3,
  normal: Vector3,
  actorKey?: string,
  basis?: TaviPlaneConfig,
) {
  if (!context.current) return;
  const { viewportId } = context.current;

  if (!actorKey) return;
  const colors = configStore.get().colors;
  const color = hexToNormalizedRGB(colors.aorta);
  syncTaviNodeActors(
    context,
    actorKey,
    world as [number, number, number],
    normal,
    color,
    basis,
  );
  requestAnimationFrame(() =>
    scheduleRender(viewportId!, { type: 'updateRenderWindow' }),
  );
}

export function getTaviCenterKeyFromActorKey(
  actorKey?: string,
): TaviCenterKey | null {
  if (!actorKey) return null;
  const normalized = actorKey.endsWith('.pick')
    ? actorKey.slice(0, -'.pick'.length)
    : actorKey;
  return TAVI_CENTER_KEY_BY_ACTOR_KEY[normalized] ?? null;
}

export function clearMeasurementActors(
  context: React.MutableRefObject<SurfaceContext | VRContext | null>,
) {
  if (!context.current) return;
  const { actorIds, renderer, overlayRenderer, viewportId } = context.current;

  const measurementActors = Object.entries(actorIds).filter(([key]) =>
    key.includes(nodeNames.measurement),
  );
  if (measurementActors.length === 0) return;

  measurementActors.forEach(([key, actor]) => {
    renderer?.removeActor(actor as vtkActor);
    overlayRenderer?.removeActor(actor as vtkActor);
    delete (actorIds as Actors)[key];
  });

  scheduleRender(viewportId!, { type: 'updateRenderWindow' });
}

export function createMeasurementActors(
  points: Vector3[],
  index: number,
  overlayRenderer: vtkRenderer,
  actorIds: Record<string, vtkActor | undefined>,
) {
  const keyName = `${nodeNames.measurement}.${index + 1}`;
  const pointsKey = `${keyName}.points`;
  const pathPolyData = createLineAndPointPolyData(points);

  let lineActor = actorIds[keyName];
  if (!lineActor) {
    lineActor = vtkActor.newInstance();
    lineActor.setMapper(vtkMapper.newInstance());
    lineActor.getProperty().setColor(1, 0.4, 0.1);
    lineActor.getProperty().setLineWidth(4);
    lineActor.getProperty().setLighting(false);
    lineActor.getProperty().setOpacity(1);
    overlayRenderer.addActor(lineActor);
    actorIds[keyName] = lineActor;
  }

  let pointActor = actorIds[pointsKey];
  if (!pointActor) {
    pointActor = vtkActor.newInstance();
    pointActor.setMapper(vtkMapper.newInstance());
    pointActor.getProperty().setColor(1, 1, 0);
    pointActor.getProperty().setOpacity(1);
    pointActor.getProperty().setLighting(false);
    (pointActor.getProperty() as any).setRepresentationToPoints?.();
    pointActor.getProperty().setPointSize(6);
    overlayRenderer.addActor(pointActor);
    actorIds[pointsKey] = pointActor;
  }

  const lineMapper =
    (lineActor.getMapper?.() as vtkMapper | null) ?? vtkMapper.newInstance();
  if (!lineActor.getMapper?.()) lineActor.setMapper(lineMapper);
  const pointMapper =
    (pointActor.getMapper?.() as vtkMapper | null) ?? vtkMapper.newInstance();
  if (!pointActor.getMapper?.()) pointActor.setMapper(pointMapper);

  lineMapper.setInputData(pathPolyData);
  lineMapper.modified?.();
  lineActor.modified?.();

  pointMapper.setInputData(pathPolyData);
  pointMapper.modified?.();
  pointActor.modified?.();

  return { lineActor, pointActor, keyName, pointsKey };
}

export function applyScaledCameraPan(
  renderer: vtkRenderer,
  renderWindow: vtkRenderWindow,
  oriCamera: {
    position: [number, number, number];
    focalPoint: [number, number, number];
    viewUp: [number, number, number];
    parallelScale: number;
  },
  isCustom: boolean = true,
) {
  const camera: vtkCamera = renderer.getActiveCamera()!;
  camera.setPosition(...oriCamera.position);
  camera.setFocalPoint(...oriCamera.focalPoint);
  camera.setViewUp(oriCamera.viewUp);
  if(isCustom)
    camera.setParallelScale(oriCamera.parallelScale); //줌비율만 적용
  renderer.resetCameraClippingRange();
  renderer.updateLightsGeometryToFollowCamera();
  renderWindow.render();
}

export function getOriCamera(renderer: vtkRenderer) {
  const camera = renderer.getActiveCamera()!;
  return {
    position: camera.getPosition().slice() as [number, number, number],
    focalPoint: camera.getFocalPoint().slice() as [number, number, number],
    viewUp: camera.getViewUp().slice() as [number, number, number],
    parallelScale: camera.getParallelScale(),
  };
}


export function applyPoolCaptureCamera({
  pool,
  renderer,
  renderWindow,
  canvas,
  modelCaptureFit,
  oriCamera,
  label = 'la',
}: {
  pool: BufferPoolItem;
  renderer: vtkRenderer;
  renderWindow: any;
  canvas: HTMLCanvasElement;
  modelCaptureFit: boolean;
  oriCamera: any;
  label?:string;
}): Vector3 | undefined {
  const result = getPoolPolyDataBounds(pool, label);
  if (!result) return undefined;

  applyScaledCameraPan(renderer, renderWindow, oriCamera, !modelCaptureFit);
  //랜더링 위치 label 중심으로
  renderer.resetCamera(result.bounds);

  if (modelCaptureFit) {
    //초기 셋팅된 스케일로 고정
    fitActorToCanvas(renderer, result.bounds, canvas);
  } else {
    //현재 보고 있는 확대사이즈만 적용
    const camera: vtkCamera = renderer.getActiveCamera()!;
    camera.setParallelScale(oriCamera.parallelScale);
    renderer.resetCameraClippingRange();
    renderer.updateLightsGeometryToFollowCamera();
    renderWindow.render();
  }
  return result.center;
}
